import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddTaskIcon from "@mui/icons-material/AddTask";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import handleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  serverErrorLogListApiCall,
  serverErrorLogDataApiCall,
  serverErrorLogResolveApiCall,
  serverErrorLogSearchApiCall,
  serverErrorLogArchiveApiCall,
  serverErrorLogCreateTaskApiCall,
} from "../../../../helpers/fakebackend_helper";

/**
 * Server Errors (CRMit Task 24900).
 *
 * Every 5xx the platform produces lands in ZCRM_Master.ServerErrorLog, one row
 * per distinct error (fingerprinted), so a failing endpoint hit a thousand
 * times is one row with a hit count rather than a thousand rows. Sources:
 *
 *   EXCEPTION - uncaught exception / PHP fatal, with file, line and trace
 *   RESPONSE  - a controller that caught its own error and returned a 5xx
 *   CLIENT    - the browser saw a 5xx PHP never logged (gateway, timeout)
 *
 * "Create Task" raises a System Change (Task 24912, was a company-3 task) exactly as the Report a Bug tool
 * does, with the whole error written into the description, and links it back
 * to the row so the same error cannot be raised twice. A resolved error that
 * happens again re-opens itself.
 */

const TAB_OPEN = "open";
const TAB_RESOLVED = "resolved";
// Task 24905 - the archive is fetched separately, not filtered out of the same
// list: /servererrorlog/list deliberately excludes archived rows, so there is
// nothing in errorList to filter.
const TAB_ARCHIVED = "archived";

const SOURCE_COLOUR = {
  EXCEPTION: "error",
  RESPONSE: "warning",
  CLIENT: "info",
};

const NO_PLATFORM = "__NONE__";
const SOURCE_FILTER_OPTIONS = ["ALL", "EXCEPTION", "RESPONSE", "CLIENT"];

const isResolved = (item) => Number(item?.FlagResolved) === 1;
const isArchived = (item) => Number(item?.FlagArchived) === 1;

// Pretty-print a stored JSON blob; anything else is shown as it is.
const formatBlob = (value) => {
  if (!value) return "";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
};

const Section = ({ title, children }) =>
  children ? (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "action.hover",
          fontFamily: "monospace",
          fontSize: "0.78rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflow: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  ) : null;

const ServerErrorLog = () => {
  const [errorList, setErrorList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_OPEN);
  const [sourceFilter, setSourceFilter] = useState("ALL");
  // Change #100075 - which front end the error came from (localhost:3000,
  // escit.portal.crmit.app, ...). Platform comes back from the API, read out
  // of the request's Origin/Referer; empty = no browser (cron, Postman).
  const [platformFilter, setPlatformFilter] = useState("ALL");
  const [keyword, setKeyword] = useState("");

  // Detail drawer
  const [detailRow, setDetailRow] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create task dialog
  const [taskTarget, setTaskTarget] = useState(null);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskNotes, setTaskNotes] = useState("");
  // Change #100140 - ticked by default: Server Errors is developer-only and most
  // 500s raised from here go straight to the AI queue.
  const [letAiHandle, setLetAiHandle] = useState(true);
  const [creatingTask, setCreatingTask] = useState(false);

  // Which row's resolve button is mid-flight.
  const [resolvingId, setResolvingId] = useState(null);

  const loadList = async () => {
    setListLoading(true);
    try {
      const response = await serverErrorLogListApiCall();
      setErrorList(response?.servererrorlog_list || []);
    } catch (e) {
      toast.error("Could not load server errors");
    }
    setListLoading(false);
  };

  useEffect(() => {
    loadList();
  }, []);

  // Task 24905 - the archive is its own fetch. /servererrorlog/list excludes
  // archived rows on purpose, so there is nothing in errorList to filter; the
  // search endpoint is asked for them by name.
  const [archivedList, setArchivedList] = useState([]);
  const [archivingId, setArchivingId] = useState(null);

  const loadArchived = async () => {
    setListLoading(true);
    try {
      const response = await serverErrorLogSearchApiCall([
        { servererrorlog_data: { FlagArchived: 1 } },
      ]);
      setArchivedList(response?.servererrorlog_list || []);
    } catch (e) {
      toast.error("Could not load the archive");
    }
    setListLoading(false);
  };

  // Only fetched when the tab is actually opened - the archive is the one view
  // nobody needs on arrival.
  useEffect(() => {
    if (activeTab === TAB_ARCHIVED) loadArchived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleArchive = async (row, archived = true) => {
    if (!row?.Id) return;
    setArchivingId(row.Id);
    try {
      const response = await serverErrorLogArchiveApiCall([
        { Id: row.Id, FlagArchived: archived ? 1 : 0 },
      ]);
      if (response?.status === 200) {
        toast.success(
          archived
            ? `Error #${row.Id} archived - it stays archived even if it happens again`
            : `Error #${row.Id} restored`
        );
        // Both lists can have changed: the row has moved between them.
        loadList();
        if (activeTab === TAB_ARCHIVED) loadArchived();
      } else {
        toast.error(response?.message || "Could not update the error");
      }
    } catch (e) {
      toast.error("Could not update the error");
    }
    setArchivingId(null);
  };

  const matchesPlatform = (item) =>
    platformFilter === "ALL" ||
    (platformFilter === NO_PLATFORM
      ? !item.Platform
      : item.Platform === platformFilter);

  const platformOptions = useMemo(() => {
    const seen = new Set();
    [...(errorList || []), ...(archivedList || [])].forEach((item) => {
      seen.add(item.Platform || NO_PLATFORM);
    });
    return ["ALL", ...Array.from(seen).sort()];
  }, [errorList, archivedList]);

  const { openRows, resolvedRows } = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    const open = [];
    const resolved = [];
    (errorList || []).forEach((item) => {
      if (sourceFilter !== "ALL" && item.Source !== sourceFilter) return;
      if (!matchesPlatform(item)) return;
      if (
        term &&
        ![
          item.Message,
          item.Route,
          item.Url,
          item.File,
          item.ExceptionClass,
          item.Platform,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      ) {
        return;
      }
      const row = { ...item, id: item.Id };
      (isResolved(item) ? resolved : open).push(row);
    });
    return { openRows: open, resolvedRows: resolved };
  }, [errorList, sourceFilter, platformFilter, keyword]);

  // Task 24905 - the archive tab reads its own list, and the same source and
  // keyword filters apply to it so it is searchable like the others.
  const archivedRows = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return (archivedList || [])
      .filter((item) => {
        if (sourceFilter !== "ALL" && item.Source !== sourceFilter) return false;
        if (!matchesPlatform(item)) return false;
        if (
          term &&
          ![
          item.Message,
          item.Route,
          item.Url,
          item.File,
          item.ExceptionClass,
          item.Platform,
        ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        ) {
          return false;
        }
        return true;
      })
      .map((item) => ({ ...item, id: item.Id }));
  }, [archivedList, sourceFilter, platformFilter, keyword]);

  const rows =
    activeTab === TAB_ARCHIVED
      ? archivedRows
      : activeTab === TAB_RESOLVED
      ? resolvedRows
      : openRows;

  // ------------------------------------------------------------------
  // Detail
  // ------------------------------------------------------------------

  // The list leaves out the request/response/trace blobs; the drawer pulls
  // the full row from data/{id}.
  const handleOpenDetail = async (row) => {
    setDetailRow(row);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const response = await serverErrorLogDataApiCall(row.Id);
      setDetailData(response?.servererrorlog_data || null);
    } catch (e) {
      toast.error("Could not load the error detail");
    }
    setDetailLoading(false);
  };

  // ------------------------------------------------------------------
  // Resolve / re-open
  // ------------------------------------------------------------------

  const handleResolve = async (row, resolved = true) => {
    if (!row?.Id) return;
    setResolvingId(row.Id);
    try {
      const response = await serverErrorLogResolveApiCall([
        { Id: row.Id, FlagResolved: resolved ? 1 : 0 },
      ]);
      if (response?.status === 200) {
        toast.success(
          resolved
            ? `Error #${row.Id} resolved - it re-opens itself if it happens again`
            : `Error #${row.Id} re-opened`
        );
        loadList();
      } else {
        toast.error(response?.message || "Could not update the error");
      }
    } catch (e) {
      toast.error("Could not update the error");
    }
    setResolvingId(null);
  };

  // ------------------------------------------------------------------
  // Create task
  // ------------------------------------------------------------------

  const defaultTaskTitle = (row) => {
    const where = row?.Route || row?.Url || "unknown route";
    const base = `${row?.HttpStatus || "5xx"} in ${where}`;
    const title = row?.Message ? `${base} - ${row.Message}` : base;
    return title.length > 250 ? `${title.slice(0, 247)}...` : title;
  };

  const handleOpenCreateTask = (row) => {
    setTaskTarget(row);
    setTaskTitle(defaultTaskTitle(row));
    setTaskNotes("");
    setLetAiHandle(true);
  };

  const handleConfirmCreateTask = async () => {
    if (!taskTarget?.Id) return;
    setCreatingTask(true);
    try {
      const response = await serverErrorLogCreateTaskApiCall([
        {
          Id: taskTarget.Id,
          Title: taskTitle,
          Notes: taskNotes,
          LetAiHandle: letAiHandle ? 1 : 0,
        },
      ]);
      if (response?.status === 200) {
        toast.success(response.message);
        // Keep an open drawer in step, so its button shows the new task.
        if (detailRow?.Id === taskTarget.Id) {
          setDetailRow((prev) => ({ ...prev, Task_Id: response.Task_Id }));
        }
        setTaskTarget(null);
        loadList();
      } else {
        toast.error(response?.message || "Could not create the task");
      }
    } catch (e) {
      toast.error("Could not create the task");
    }
    setCreatingTask(false);
  };

  // ------------------------------------------------------------------
  // Grid
  // ------------------------------------------------------------------

  const columns = [
    {
      field: "LastSeenDateTime",
      headerName: "Last Seen",
      width: 165,
      renderCell: (params) =>
        params.value ? handleDateTime(params.value) : "-",
    },
    {
      field: "Source",
      headerName: "Source",
      width: 115,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={SOURCE_COLOUR[params.value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "Platform",
      headerName: "Platform",
      width: 190,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.row.PlatformUrl || ""}>
            <Chip
              size="small"
              label={params.value}
              color={
                /^(localhost|127\.0\.0\.1)(:|$)/.test(params.value)
                  ? "warning"
                  : "default"
              }
              variant="outlined"
            />
          </Tooltip>
        ) : (
          "-"
        ),
    },
    { field: "HttpStatus", headerName: "HTTP", width: 70 },
    {
      field: "Route",
      headerName: "Where",
      width: 240,
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.File
              ? `${params.row.File}:${params.row.Line}`
              : params.row.Url || ""
          }
        >
          <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
            {params.value || params.row.Url || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "Message",
      headerName: "Message",
      flex: 1,
      minWidth: 260,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <Typography variant="body2" noWrap>
            {params.value || params.row.ExceptionClass || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "HitCount",
      headerName: "Hits",
      width: 80,
      type: "number",
      renderCell: (params) => (
        <Tooltip
          title={`First seen ${
            params.row.FirstSeenDateTime
              ? handleDateTime(params.row.FirstSeenDateTime)
              : "-"
          }${
            Number(params.row.CountReopened) > 0
              ? ` - re-opened ${params.row.CountReopened} time(s)`
              : ""
          }`}
        >
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "Company_Id",
      headerName: "Company",
      width: 90,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "Task_Id",
      headerName: "Task",
      width: 90,
      renderCell: (params) => (params.value ? `#${params.value}` : "-"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View request, response and stack trace">
            <IconButton size="small" onClick={() => handleOpenDetail(params.row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              params.row.Task_Id
                ? `Task #${params.row.Task_Id} already raised`
                : "Create a System Change for this error"
            }
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={!!params.row.Task_Id}
                onClick={() => handleOpenCreateTask(params.row)}
              >
                <AddTaskIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isResolved(params.row) ? "Re-open" : "Mark resolved"}>
            <span>
              <IconButton
                color={isResolved(params.row) ? "primary" : "success"}
                size="small"
                disabled={resolvingId === params.row.Id}
                onClick={() =>
                  handleResolve(params.row, !isResolved(params.row))
                }
              >
                {isResolved(params.row) ? <ReplayIcon /> : <CheckCircleIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {/* Task 24905 - archive, or restore from the archive tab. Separate
              from resolve on purpose: resolved re-opens itself when the error
              happens again, archived does not. */}
          <Tooltip
            title={
              isArchived(params.row)
                ? "Restore to Open"
                : "Archive - hides it, and it stays hidden even if it happens again"
            }
          >
            <span>
              <IconButton
                color={isArchived(params.row) ? "primary" : "default"}
                size="small"
                disabled={archivingId === params.row.Id}
                onClick={() =>
                  handleArchive(params.row, !isArchived(params.row))
                }
              >
                {isArchived(params.row) ? (
                  <UnarchiveIcon />
                ) : (
                  <ArchiveIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Server Errors"
      description="Every HTTP 500 across the platform, captured with the request, response and stack trace so it can be fixed before anyone reports it. One row per distinct error with a hit count. Create Task raises a System Change (Super Admin > System Changes) the same way Report a Bug does; a resolved error re-opens itself if it happens again."
      tabs={[
        { value: TAB_OPEN, label: "Open", count: openRows.length },
        { value: TAB_RESOLVED, label: "Resolved", count: resolvedRows.length },
        // Task 24905 - the count is only known once the tab has been opened,
        // because the archive is fetched on demand. Showing a stale or invented
        // number would be worse than showing none.
        {
          value: TAB_ARCHIVED,
          label: "Archived",
          count: archivedList.length || undefined,
        },
      ]}
      value={activeTab}
      onChange={setActiveTab}
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold">
            Server Errors
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search message, route, file..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField
              size="small"
              select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {SOURCE_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ALL" ? "All sources" : option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {platformOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ALL"
                    ? "All platforms"
                    : option === NO_PLATFORM
                    ? "No browser"
                    : option}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadList}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={listLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => handleOpenDetail(params.row)}
            sx={{ border: 0 }}
          />
        </Paper>

        {/* ---------------------------------------------------------- */}
        {/* Create task                                                */}
        {/* ---------------------------------------------------------- */}
        <Dialog
          open={!!taskTarget}
          onClose={() => !creatingTask && setTaskTarget(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create a System Change</DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ mb: 2 }}>
              Raised in Super Admin > System Changes, the same way Report a Bug does it. The
              error, request, response and stack trace are written into the
              task description.
            </DialogContentText>
            <TextField
              margin="dense"
              label="Title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Notes (optional, added above the error detail)"
              value={taskNotes}
              onChange={(e) => setTaskNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={letAiHandle}
                  onChange={(e) => setLetAiHandle(e.target.checked)}
                />
              }
              label="Let AI handle this (queue straight for AI development)"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setTaskTarget(null)}
              color="inherit"
              disabled={creatingTask}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmCreateTask}
              disabled={creatingTask || !taskTitle.trim()}
            >
              {creatingTask ? "Creating..." : "Create Task"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------------------------------------------------- */}
        {/* Detail                                                     */}
        {/* ---------------------------------------------------------- */}
        <Drawer
          anchor="right"
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          PaperProps={{ sx: { width: { xs: "100%", md: 820 } } }}
        >
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">
                  Error #{detailRow?.Id}{" "}
                  <Chip
                    size="small"
                    label={detailRow?.Source}
                    color={SOURCE_COLOUR[detailRow?.Source] || "default"}
                    variant="outlined"
                  />
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-word" }}
                >
                  {detailRow?.Message || detailRow?.ExceptionClass}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddTaskIcon />}
                  disabled={!!detailRow?.Task_Id}
                  onClick={() => handleOpenCreateTask(detailRow)}
                >
                  {detailRow?.Task_Id
                    ? `Task #${detailRow.Task_Id}`
                    : "Create Task"}
                </Button>
                <IconButton size="small" onClick={() => setDetailRow(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {detailLoading && <CircularProgress size={22} />}

            {!detailLoading && detailData && (
              <>
                <Section title="Summary">
                  {[
                    `HTTP ${detailData.HttpStatus || "-"}  ${detailData.HttpMethod || ""} ${detailData.Url || ""}`,
                    detailData.Route && `Route: ${detailData.Route}`,
                    detailData.ExceptionClass &&
                      `Exception: ${detailData.ExceptionClass}`,
                    detailData.File &&
                      `File: ${detailData.File}:${detailData.Line}`,
                    detailData.Host && `Host: ${detailData.Host}`,
                    detailData.PlatformUrl &&
                      `Platform: ${detailData.PlatformUrl}`,
                    `Company: ${detailData.Company_Id || "-"} (${detailData.DatabaseName || "-"})   User: ${detailData.User_Id || "-"}`,
                    `Hits: ${detailData.HitCount}   First: ${detailData.FirstSeenDateTime || "-"}   Last: ${detailData.LastSeenDateTime || "-"}`,
                    Number(detailData.CountReopened) > 0 &&
                      `Re-opened ${detailData.CountReopened} time(s)`,
                  ]
                    .filter(Boolean)
                    .join("\n")}
                </Section>
                <Section title="Message">{detailData.Message}</Section>
                <Section title="Stack trace">{detailData.StackTrace}</Section>
                <Section title="Request body (latest occurrence, sensitive keys masked)">
                  {formatBlob(detailData.RequestBody)}
                </Section>
                <Section title="Response body">
                  {formatBlob(detailData.ResponseBody)}
                </Section>
                <Section title="Request headers">
                  {formatBlob(detailData.RequestHeaders)}
                </Section>
                <Section title="Client context">
                  {formatBlob(detailData.ClientContext)}
                </Section>
              </>
            )}
          </Box>
        </Drawer>
      </Box>
    </SettingsPanel>
  );
};

export default ServerErrorLog;
